import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { showLatest } from './chartShift';
import { FibPrimitive, type FibDrawing } from './fibPrimitive';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

/** A point on the chart, snapped to a bar and to one of its extremes. */
export interface ChartPoint {
  time: number;
  price: number;
}

interface FibChartProps {
  candles: Candle[];
  /** The swings and their levels; empty draws candles alone. */
  drawings: FibDrawing[];
  digits: number;
  theme: 'dark' | 'light';
  resetKey?: string;
  className?: string;
  visibleBars?: number;
  /**
   * Draw mode: clicks pick points instead of doing nothing.
   *
   * A click lands on the bar under the pointer and snaps to whichever of its
   * high or low is nearer — a swing's ends are extremes, and nobody can hit a
   * wick tip to the pixel.
   */
  drawMode?: boolean;
  onPick?: (point: ChartPoint) => void;
  /** Pointer position while in draw mode, for a preview of the draw in progress. */
  onHover?: (point: ChartPoint | null) => void;
}

/** Stored timestamps are broker wall-clock, so they must be read as UTC. */
function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * Candles with Fibonacci retracements drawn over them.
 *
 * Same shell as KnotChart; the drawings are a series primitive because the
 * levels are prices and a swing is two price points, and only the series can
 * map those to pixels. The candles can be any timeframe; the swings drawn
 * over them may come from every timeframe at once.
 */
export function FibChart({
  candles,
  drawings,
  digits,
  theme,
  resetKey,
  className,
  visibleBars = 200,
  drawMode = false,
  onPick,
  onHover,
}: FibChartProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  useLiveBar(seriesRef, candles);
  const primitiveRef = useRef<FibPrimitive | null>(null);
  const positionedForRef = useRef<string | null>(null);

  // Latest values for the chart's own event handlers, which are bound once.
  const candlesRef = useRef(candles);
  candlesRef.current = candles;
  const drawModeRef = useRef(drawMode);
  drawModeRef.current = drawMode;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const palette = chartPalette(theme);
    const chart = createChart(host, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: palette.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    const primitive = new FibPrimitive();
    series.attachPrimitive(primitive);

    chartRef.current = chart;
    seriesRef.current = series;
    primitiveRef.current = primitive;

    /** The bar under the pointer, snapped to its nearer extreme. */
    const snap = (time: unknown, y: number | undefined): ChartPoint | null => {
      if (typeof time !== 'number' || y === undefined) return null;
      const bar = candlesRef.current.find((c) => c.time === time);
      if (!bar) return null;
      const price = series.coordinateToPrice(y);
      if (price === null) return null;
      const nearHigh = Math.abs(bar.high - price) <= Math.abs(price - bar.low);
      return { time, price: nearHigh ? bar.high : bar.low };
    };

    chart.subscribeClick((param) => {
      if (!drawModeRef.current) return;
      const point = snap(param.time, param.point?.y);
      if (point) onPickRef.current?.(point);
    });
    chart.subscribeCrosshairMove((param) => {
      if (!drawModeRef.current || !onHoverRef.current) return;
      onHoverRef.current(snap(param.time, param.point?.y));
    });

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      primitiveRef.current = null;
      positionedForRef.current = null;
    };
  }, [theme, digits]);

  useEffect(() => {
    seriesRef.current?.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
  }, [candles]);

  useEffect(() => {
    primitiveRef.current?.setDrawings(drawings);
  }, [drawings]);

  // Position once per series — re-centring on every refetch would yank the
  // view out from under anyone who had scrolled back.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || candles.length === 0) return;

    const key = resetKey ?? '';
    if (positionedForRef.current === key) return;
    positionedForRef.current = key;

    showLatest(chart.timeScale(), candles.length - 1, visibleBars);
  }, [resetKey, candles, visibleBars]);

  return (
    <div
      ref={hostRef}
      className={cn('relative', drawMode && '[&_canvas]:cursor-crosshair', className)}
    />
  );
}
